<template>
  <div class="pt-4 overflow-y-auto relative body pb-24">
    <div class="px-6 flex-grow">
      <div class="space-y-5">
        <TextField label="Nama Perusahaan" v-model="form.name" :required="true" />

        <TextField label="Nama Admin Pengirim" v-model="form.sender_name" :required="true" />

        <TextField label="Email" v-model="form.email" :required="true" disabled />

        <div>
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">
            Alamat Pengirim
          </label>
          <textarea
            v-model="form.pickup_address"
            rows="3"
            class="w-full border border-neutral-300 rounded-md p-2 text-sm focus:outline-none focus:border-primary-700"
            placeholder="Masukkan alamat lengkap penjemputan"
          ></textarea>
        </div>

        <TextField label="Petunjuk Pengiriman" v-model="form.detail_address" placeholder="Masukan petunjuk sebagai patokan" />

        <div class="relative">
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">
            Kecamatan / Kelurahan / Kodepos
          </label>

          <TextField type="text" v-model="areaSearch" placeholder="Cari kecamatan / kodepos" @input="searchArea" />

          <div class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-neutral-400 pointer-events-none">
            <div v-if="isSearching" class="animate-spin h-4 w-4 border-2 border-primary-700 border-t-transparent rounded-full"></div>
            <Search v-else class="w-5 h-5" />
          </div>

          <div v-if="showAreaSuggestion" class="absolute z-20 mt-1 w-full bg-white border rounded-md max-h-48 overflow-y-auto">
            <ul v-if="cities?.length">
              <li v-for="(item, index) in cities" :key="index" class="px-3 py-2 cursor-pointer hover:bg-neutral-100 text-sm" @click="selectArea(item)">{{ item.village }}, {{ item.area_display }}</li>
            </ul>
            <div v-else class="p-3 text-sm text-neutral-500">
              Data tidak ditemukan
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <TextField label="Latitude" v-model="form.lat" placeholder="-6.xxxx" />
          <TextField label="Longitude" v-model="form.lng" placeholder="107.xxxx" />
        </div>

        <TextField label="No. Handphone" v-model="form.phone_number" :required="true" />

        <div>
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">
            Volume Transaksi
          </label>
          <CustomSelect :options="volumeList" optionLabel="label" optionValue="value" v-model="selectedVolume" placeholder="Pilih estimasi volume" />
        </div>

        <TextField label="Bidang Usaha" v-model="form.business_field" placeholder="Contoh: Retail, Manufaktur" />

        <div>
          <label class="text-sm text-neutral-700 font-bold mb-3 block border-b pb-2">
            Kategori Barang
          </label>
          <div class="grid grid-cols-2 gap-y-2">
            <label v-for="item in goodsCategories" :key="item.code" class="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" :value="item.name" v-model="form.categories" class="w-4 h-4 accent-primary-700" />
              <span>{{ item.name }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="px-6 pb-6 bg-white fixed bottom-0 left-0 z-10 w-full border-t pt-4">
      <Button buttonText="Update Profil Mitra" size="full" @action="submitForm" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'PartnerProfileForm',

  components: {
    TextField: () => import('@/components/Forms/TextField'),
    CustomSelect: () => import('@/components/Forms/CustomSelect'),
    Button: () => import('@/components/Forms/Button'),
    Search: () => import('@/components/Icons/Search.vue')
  },

  data() {
    return {
      selectedVolume: '',

      areaSearch: '',
      areaCode: null,
      debounce: null,
      isSearching: false,
      showAreaSuggestion: false,

      form: {
        id: '',
        name: '',
        sender_name: '',
        email: '',
        pickup_address: '',
        detail_address: '',
        area_display: '',
        lat: '',
        lng: '',
        phone_number: '',
        business_field: '',
        categories: []
      },

      volumeList: [
        { label: '< 100', value: '< 100' },
        { label: '100 - 500', value: '100 - 500' },
        { label: '> 500', value: '> 500' }
      ],

      categoryOptions: ['Aksesoris', 'Alat Kesehatan', 'Makanan Tahan Lama', 'Otomotif', 'Dokumen', 'Lainnya']
    }
  },

  computed: {
    ...mapGetters('auth', ['userData']),
    ...mapGetters('merchant', ['mitraDetail']),
    ...mapGetters('master', ['goodsCategories', 'cities']),

    user() {
      return this.userData?.user
    }
  },

  watch: {
    mitraDetail: {
      immediate: true,
      handler(newData) {
        if (!newData) return
        this.mapDetailToForm(newData)
      }
    }
  },

  methods: {
    ...mapActions('common', ['showLoading', 'hideLoading', 'setToast']),
    ...mapActions('merchant', ['getMitraDetail', 'updateMitraDetail']),
    ...mapActions('master', ['getGoodsCategories', 'getCities']),

    searchArea(value) {
      this.areaCode = null
      this.showAreaSuggestion = false
      clearTimeout(this.debounce)

      if (!value) return

      this.isSearching = true

      this.debounce = setTimeout(async () => {
        try {
          await this.getCities({
            params: {
              page: 1,
              per_page: 20,
              search_keyword: value
            }
          })
          this.showAreaSuggestion = true
        } finally {
          this.isSearching = false
        }
      }, 300)
    },

    selectArea(area) {
      this.areaCode = area.code
      this.areaSearch = `${area.village}, ${area.area_display}`
      this.form.area_display = this.areaSearch
      this.showAreaSuggestion = false
    },

    async fetchGoodsCategories() {
      try {
        await this.getGoodsCategories()
      } catch (e) {
        this.setToast({
          type: 'error',
          message: 'Gagal memuat kategori barang'
        })
      }
    },

    async fetchPartnerDetail() {
      if (!this.user?.id) return

      this.showLoading()
      try {
        await this.getMitraDetail({ externalId: this.user.id })
      } catch (e) {
        this.setToast({ type: 'error', message: 'Gagal memuat data mitra' })
      } finally {
        this.hideLoading()
      }
    },

    mapDetailToForm(data) {
      this.form.id = data.id ?? ''
      this.form.name = data.name ?? ''
      this.form.sender_name = data.sender_name ?? ''
      this.form.email = data.email ?? ''
      this.form.pickup_address = data.address ?? ''
      this.form.detail_address = data.detail_address ?? ''
      this.form.phone_number = data.phone_number ?? ''
      this.form.business_field = data.business_field ?? ''
      this.selectedVolume = data.volume_transaction ?? ''
      this.areaCode = data.area_code ?? null
      this.areaSearch = [data.subdistrict, data.district, data.province, data.postal_code].filter(Boolean).join(', ')
      this.form.area_display = this.areaSearch

      if (typeof data.latlong === 'string' && data.latlong.includes(',')) {
        const [lat, lng] = data.latlong.split(',')
        this.form.lat = lat.trim()
        this.form.lng = lng.trim()
      } else {
        this.form.lat = ''
        this.form.lng = ''
      }

      this.form.categories = data.categories ? data.categories.split(',').map(i => i.trim()) : []
    },

    async submitForm() {
      this.showLoading()

      try {
        await this.updateMitraDetail({
          externalId: this.user.id,
          payloads: {
            name: this.form.name,
            sender_name: this.form.sender_name,
            email: this.form.email,
            phone_number: this.form.phone_number,
            pickup_address: this.form.pickup_address,
            address: this.form.pickup_address,
            detail_address: this.form.detail_address,
            business_field: this.form.business_field,
            volume_transaction: this.selectedVolume,
            categories: this.form.categories.join(', '),
            latitude: this.form.lat,
            longitude: this.form.lng,
            area_code: this.areaCode
          }
        })

        this.setToast({
          options: {
            type: 'success',
            primaryText: 'Update Berhasil',
            secondaryText: 'Profil mitra berhasil diperbarui'
          }
        })
      } catch (e) {
        const status = e?.response?.status
        const message = e?.response?.data?.message

        if (status === 422) {
          this.setToast({
            options: {
              type: 'danger',
              primaryText: 'Validasi Gagal',
              secondaryText: message || 'Periksa kembali data yang diinput'
            }
          })
        } else {
          this.setToast({
            options: {
              type: 'danger',
              primaryText: 'Update Gagal',
              secondaryText: message || 'Terjadi kesalahan sistem'
            }
          })
        }
      } finally {
        this.hideLoading()
      }
    }
  },

  mounted() {
    this.fetchPartnerDetail(), this.fetchGoodsCategories()
  }
}
</script>

<style scoped>
.body {
  height: calc(100% - 52px);
}
textarea {
  resize: none;
}
</style>
